import React from 'react';
import { FileText, Download, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { TranslationResponse } from '@/types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FileUploadSectionProps {
  fileMode: boolean;
  isTranslating: boolean;
  translationResult: TranslationResponse | null;
  onFileModeChange: (enabled: boolean) => void;
  onDownload: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  fileMode,
  isTranslating,
  translationResult,
  onFileModeChange,
  onDownload,
}) => {
  const { t } = useTranslation();

  const handleFileModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[FileUploadSection] fileMode checkbox changed to:', e.target.checked);
    onFileModeChange(e.target.checked);
  };

  return (
    <div className="space-y-4">
      {/* File mode toggle */}
      <div className="flex items-center space-x-2 p-4 border border-blue-500/30 rounded-lg bg-blue-900/20">
        <input
          type="checkbox"
          id="fileMode"
          checked={fileMode}
          onChange={handleFileModeChange}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          disabled={isTranslating}
        />
        <label
          htmlFor="fileMode"
          className="text-sm font-medium text-white/70 flex items-center space-x-2"
        >
          <FileText className="h-4 w-4 text-blue-600" />
          <span>{t('translate.fields.fileMode', { defaultValue: 'Output as File(s)' })}</span>
        </label>
        <span className="text-xs text-white/50 ml-2">
          {t('translate.fields.fileModeDescription', {
            defaultValue: 'Download translations as separate files',
          })}
        </span>
      </div>

      {/* Free Translation Badge */}
      <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-green-300">
              {t('translate.freeTranslation.title', { defaultValue: 'Free Translation' })}
            </p>
            <p className="text-sm text-green-400">
              {t('translate.freeTranslation.description', {
                defaultValue: 'NLLB-200 translation is completely free. No tokens required!',
              })}
            </p>
          </div>
          <span className="px-3 py-1 bg-green-900/40 text-green-300 text-sm font-semibold rounded-full">
            {t('translate.freeWithNLLB', { defaultValue: 'FREE' })}
          </span>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-200">
            <p className="font-medium mb-1 text-blue-300">
              {t('translate.info.title', { defaultValue: 'About NLLB Translation' })}
            </p>
            <p className="text-blue-200/80">
              {t('translate.info.description', {
                defaultValue:
                  "Powered by Meta's NLLB-200 model supporting 146 languages. Translations are processed locally for privacy. Enable AI Boost for higher quality translations with context awareness.",
              })}
            </p>
          </div>
        </div>
      </div>

      {/* File mode results - shown after a file-mode translation completes */}
      {translationResult && translationResult.file_mode && translationResult.project_folder && (
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-medium text-green-300">
                  {t('translate.results.filesSaved', {
                    defaultValue: 'Files saved to project folder',
                  })}
                </p>
                <p className="text-sm text-green-600 font-mono">
                  temp/{translationResult.project_folder}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onDownload}
              className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>{t('translate.buttons.downloadZip', { defaultValue: 'Download ZIP' })}</span>
            </button>
          </div>
          {translationResult.output_files && (
            <div className="mt-3 pt-3 border-t border-green-500/30">
              <p className="text-xs text-green-600 mb-2">
                {t('translate.results.filesCreated', {
                  defaultValue: 'Files created:',
                })}
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.keys(translationResult.output_files).map((key) => (
                  <span
                    key={key}
                    className="px-2 py-1 bg-green-900/40 text-green-300 text-xs rounded font-mono"
                  >
                    {key === 'source'
                      ? `source_${translationResult.source_language}.txt`
                      : `translation_${key}.txt`}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUploadSection;
